import React from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";

const ACCENT = "#0f766e";

const PRINT = `
  radial-gradient(circle at 28% 32%, #e7b3c8 0 11px, transparent 12px),
  radial-gradient(circle at 68% 58%, #f3d7a1 0 13px, transparent 14px),
  radial-gradient(circle at 46% 74%, #c5b6e0 0 9px, transparent 10px),
  linear-gradient(#f7f3ee, #f7f3ee)
`;

const dropIn = keyframes`
  0% { transform: translateY(-18px); opacity: 0; }
  18%, 78% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(8px); opacity: 0; }
`;

const sliderPulse = keyframes`
  0%, 20% { width: 38%; }
  50%, 70% { width: 72%; }
  100% { width: 38%; }
`;

const seamClose = keyframes`
  0%, 16% { transform: scaleX(1); opacity: 0.95; }
  42%, 100% { transform: scaleX(0); opacity: 0; }
`;

const repeatIn = keyframes`
  0%, 40% { opacity: 0; }
  58%, 86% { opacity: 1; }
  100% { opacity: 0; }
`;

const historyIn = keyframes`
  0%, 25% { opacity: 0; transform: translateY(8px); }
  45%, 80% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
};

function Stage({ children }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 118,
        overflow: "hidden",
        bgcolor: "#f3efe8",
      }}
    >
      {children}
    </Box>
  );
}

function PhotoScene() {
  return (
    <Stage>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 16,
          width: 92,
          height: 78,
          ml: "-46px",
          borderRadius: 1.5,
          backgroundImage: PRINT,
          boxShadow: "0 8px 18px rgba(80,60,40,0.12)",
          animation: `${dropIn} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function OptionsScene() {
  return (
    <Stage>
      <Box
        sx={{
          position: "absolute",
          left: 26,
          right: 26,
          top: 36,
          display: "grid",
          gap: 1.6,
        }}
      >
        {[0, 1].map((i) => (
          <Box key={i}>
            <Box
              sx={{
                height: 6,
                borderRadius: 99,
                bgcolor: alpha(ACCENT, 0.16),
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  borderRadius: 99,
                  bgcolor: ACCENT,
                  animation: `${sliderPulse} 4.8s ease-in-out infinite`,
                  animationDelay: `${i * 0.35}s`,
                  ...motionSafe,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Stage>
  );
}

function GenerateScene() {
  return (
    <Stage>
      <CropFreeOutlinedIcon
        sx={{
          position: "absolute",
          left: 18,
          top: 46,
          color: ACCENT,
          fontSize: 22,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 48,
          top: 18,
          width: 64,
          height: 82,
          backgroundImage: PRINT,
          overflow: "hidden",
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: 0,
            width: 10,
            height: "100%",
            ml: "-5px",
            bgcolor: "#f6f1e8",
            transformOrigin: "center",
            animation: `${seamClose} 4.8s ease-in-out infinite`,
            ...motionSafe,
          }}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 16,
          top: 18,
          width: 78,
          height: 82,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2px",
          animation: `${repeatIn} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={{ backgroundImage: PRINT, borderRadius: 0.5 }} />
        ))}
      </Box>
    </Stage>
  );
}

function HistoryScene() {
  return (
    <Stage>
      <Box
        sx={{
          position: "absolute",
          left: 18,
          top: 18,
          width: 78,
          height: 82,
          borderRadius: 1,
          backgroundImage: PRINT,
          border: "1px solid",
          borderColor: alpha(ACCENT, 0.28),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 16,
          top: 20,
          width: 92,
          display: "grid",
          gap: 0.7,
          animation: `${historyIn} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              height: 22,
              borderRadius: 1,
              bgcolor: "#fffdf8",
              border: "1px solid",
              borderColor: alpha(ACCENT, 0.2),
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 0.6,
              opacity: 1 - i * 0.18,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: 0.4,
                backgroundImage: PRINT,
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 99,
                bgcolor: alpha(ACCENT, 0.18),
              }}
            />
          </Box>
        ))}
      </Box>
    </Stage>
  );
}

function SceneCard({ preview, title, body }) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: alpha(ACCENT, 0.16),
        bgcolor: "background.paper",
        boxShadow: `0 10px 28px ${alpha(ACCENT, 0.06)}`,
      }}
    >
      {preview}
      <Box sx={{ p: 1.5 }}>
        <Typography fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.55, lineHeight: 1.45 }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

export default function SkillAgentInterpretHelpDialog({ open, onClose }) {
  const { t } = useTranslation();
  const key = "skillAgent.usageHelp.interpretacion";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>
          {t(`${key}.title`)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t(`${key}.lead`)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 1.25,
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <SceneCard
            preview={<PhotoScene />}
            title={t(`${key}.scenePhotoTitle`)}
            body={t(`${key}.scenePhotoBody`)}
          />
          <SceneCard
            preview={<OptionsScene />}
            title={t(`${key}.sceneOptionsTitle`)}
            body={t(`${key}.sceneOptionsBody`)}
          />
          <SceneCard
            preview={<GenerateScene />}
            title={t(`${key}.sceneGenerateTitle`)}
            body={t(`${key}.sceneGenerateBody`)}
          />
          <SceneCard
            preview={<HistoryScene />}
            title={t(`${key}.sceneHistoryTitle`)}
            body={t(`${key}.sceneHistoryBody`)}
          />
        </Box>
        <Box
          sx={{
            mt: 1.5,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            bgcolor: alpha(ACCENT, 0.06),
            display: "grid",
            gap: 1.1,
          }}
        >
          {[
            ["noteSizeTitle", "noteSize"],
            ["noteDownloadsTitle", "noteDownloads"],
            ["noteHistoryTitle", "noteHistory"],
          ].map(([title, body]) => (
            <Box key={title}>
              <Typography fontWeight={800} sx={{ fontSize: "0.84rem" }}>
                {t(`${key}.${title}`)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.3, lineHeight: 1.45 }}
              >
                {t(`${key}.${body}`)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.25, lineHeight: 1.45 }}
        >
          {t(`${key}.againHint`)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
        >
          {t("skillAgent.usageHelp.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
